import { Location as ALocation } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { FavoriteFolderService } from 'src/api/activity/favorite-folder/favorite-folder.service';
import { I18nTitleStrategy } from 'src/app/utils/i18n-title-strategy';
import { TranslateService } from 'src/app/utils/translate.service';
import { BrowseOrganizationUnitDialogComponent, IBrowseOrganizationUnitDialogData, ISelectedOrganizationUnit } from 'src/components/dialogs/browse-organization-unit-dialog/browse-organization-unit-dialog.component';
import { ConfirmationDialogComponent } from 'src/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ActivitySelectionProvider } from 'src/providers/activity-selection.provider';
import { PermissionProvider } from 'src/providers/permission.provider';
import { PinbordProvider } from 'src/providers/pinbord.provider';
import { ProgramCreationProvider } from 'src/providers/program-creation.provider';
import { DoenkidsSessionProvider } from 'src/providers/session.provider';
import { IFavoriteFolderDetails, ISearchActivity } from 'typings/doenkids/doenkids';

@Component({
    selector: 'app-pinbord-folder-details',
    templateUrl: './pinbord-folder-details.component.html',
    styleUrls: ['./pinbord-folder-details.component.scss'],
    standalone: false
})
export class PinbordFolderDetailsComponent implements OnInit, OnDestroy {
  private stop$: Subject<void> = new Subject<void>();

  public hasOUWritePermission$: Observable<boolean>;

  public hasParentOUWritePermission$: Observable<boolean>;

  public currentFolder: IFavoriteFolderDetails;

  public selectActivitiesControl: UntypedFormControl = new UntypedFormControl(false);

  public isSelectingActivties$: Observable<boolean>;

  public isReader$: Observable<boolean>;

  public isAdmin$: Observable<boolean>;

  private fetchShares$ = new BehaviorSubject<void>(undefined);

  public currentOuId$: Observable<number>;

  public shares$ = this.fetchShares$.pipe(
    takeUntil(this.stop$),
    switchMap(async () => {
      try {
        const result = await this.$favoriteFolder.fetchAllShares(this.currentFolder.id);

        return result.items;

      } catch (error) {
        console.error('Error fetching shares:', error);
        return [];
      }
    }),
    shareReplay(1),
  );

  public sharesBadge$ = this.shares$.pipe(
    map((shares) => {
      if (shares.length === 0) {
        return null;
      }
      return shares.length > 99 ? '99+' : shares.length.toString();
    }),
  );

  constructor(
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private location: ALocation,
    $permission: PermissionProvider,
    private $pinbord: PinbordProvider,
    private $favoriteFolder: FavoriteFolderService,
    private $activitySelection: ActivitySelectionProvider,
    private $programCreate: ProgramCreationProvider,
    private $session: DoenkidsSessionProvider,
    private $translateService: TranslateService,
    private readonly $i18nTitleStrategy: I18nTitleStrategy,
  ) {
    this.hasOUWritePermission$ = $permission.hasOUWritePermissions$.pipe(takeUntil(this.stop$));
    this.hasParentOUWritePermission$ = $permission.hasParentOUWritePermissions$;
    this.isReader$ = this.$session.isReader$.pipe(takeUntil(this.stop$));
    this.isSelectingActivties$ = this.$activitySelection.isSelecting$.asObservable().pipe(takeUntil(this.stop$));
    this.isAdmin$ = this.$session.isAdmin$;
  }

  ngOnInit(): void {
    this.$pinbord.currentFolders$.pipe(takeUntil(this.stop$)).subscribe((currentFolders) => {
      if (currentFolders?.folders) {
        this.fetchFolder(currentFolders.folders);
      }
    });

    // If the checkbox for collecting activities gets toggled, toggle the status.
    //
    this.selectActivitiesControl.valueChanges.pipe(takeUntil(this.stop$)).subscribe((isSelecting) => {
      if (isSelecting !== this.$activitySelection.isSelecting$.value) {
        this.$activitySelection.toggleSelecting();
      }
    });

    this.$activitySelection.isSelecting$.pipe(takeUntil(this.stop$)).subscribe((isSelecting) => {
      if (isSelecting !== this.selectActivitiesControl.value) {
        this.selectActivitiesControl.setValue(isSelecting);
      }
    });

    this.currentOuId$ = this.$session.currentOuId$;
  }

  ngOnDestroy() {
    this.stop$.next();
  }

  fetchFolder(folders: IFavoriteFolderDetails[]) {
    // Get a snapshot of the current route parameters.
    //
    const { params } = this.route.snapshot;
    this.currentFolder = folders.find((folder) => folder.id === parseInt(params.id, 10));
    this.$i18nTitleStrategy.updateTitleParameters({ pinboard: this.currentFolder?.name ?? '' });
  }

  async deletePinbordFolder() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      minWidth: '320px',
      data: {
        title: this.$translateService.instant(_('generic.remove')),
        description: this.$translateService.instant(_('pinboard.folder.delete.confirm.dialog.description')),
      },
    });

    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result === 'confirm') {
      const currentOUId = await firstValueFrom(this.$session.currentOuId$);
      await this.$pinbord.removeFolder(currentOUId, this.currentFolder.id);
      await this.$pinbord.fetchFolders(currentOUId);
      if (this.router.navigated) {
        this.location.back();
      } else {
        this.router.navigate(['/']);
      }
    }
  }

  activityClicked(activity: ISearchActivity) {
    if (this.selectActivitiesControl.value) {
      this.$activitySelection.toggleActivitySelected(activity);
      return;
    }

    this.router.navigate([`/activities/preview/${activity.id}`]);
  }

  activityIsSelected(activity: ISearchActivity) {
    const isSelected = this.$activitySelection.selectedActivities$.value.find((selectedActivity) => selectedActivity.id === activity.id);
    return isSelected;
  }

  async createProgram() {
    this.$programCreate.promptProgramFromPinbord(this.currentFolder);
  }

  goToActivities() {
    if (!this.$activitySelection.isSelecting$.value) {
      this.$activitySelection.toggleSelecting();
    }
    this.router.navigate(['/activities']);
  }


  async share() {
    const shares = await firstValueFrom(this.shares$);

    const selectedOrganizationUnits: ISelectedOrganizationUnit[] = shares.map((share) => ({
      id: share.organization_unit_id,
      name: share.organization_unit_name,
      organization_unit_type_id: share.organization_unit_type_id,
    }));

    const currentOrganizationUnitId = await firstValueFrom(this.$session.currentOuId$);

    const browseOrganizationUnitDialog = this.dialog.open(BrowseOrganizationUnitDialogComponent, {
      width: '600px',
      minWidth: '420px',
      data: {
        title: this.$translateService.instant(_('insights_comparison.browse_organization_dialog.title')),
        initialOrganizationUnitSelection: selectedOrganizationUnits,
        disabledOrganizationUnitIds: [currentOrganizationUnitId],
        allowMultipleSelection: true,
        minSelectedOus: 0,
      } as IBrowseOrganizationUnitDialogData,
    });

    const results: ISelectedOrganizationUnit[] = await firstValueFrom(browseOrganizationUnitDialog.afterClosed());

    if (!results) return;

    const organizationUnitsToUnselect = shares.filter((share) => !results.some((result) => result.id === share.organization_unit_id));
    const organizationUnitsToSelect = results.filter((result) => !shares.some((share) => share.organization_unit_id === result.id));

    if (organizationUnitsToUnselect.length > 0) {
      const promises = organizationUnitsToUnselect.map((share) => this.$favoriteFolder.unshare(this.currentFolder.id, share.organization_unit_id));
      await Promise.all(promises);
    }

    if (organizationUnitsToSelect.length > 0) {
      const promises = organizationUnitsToSelect.map((organizationUnit) => this.$favoriteFolder.share(this.currentFolder.id, organizationUnit.id));
      await Promise.all(promises);
    }

    this.fetchShares$.next();
  }
}
